// NodeTypeComponents.js

// -----------------------------------------------------------------------------
// Component Imports (alphabetized within groups)
// -----------------------------------------------------------------------------

import { getNodeTypesCache } from 'services/nodetypes/NodeTypesCacheService';

// Article
import ArticleEditor from 'nodetypes/article/highlight/ArticleEditor';

// Document
import DocumentView from 'nodetypes/document/view/DocumentView';
import DocumentSummary from 'nodetypes/document/summary/DocumentSummary';

// File
import FileSummary from 'nodetypes/file/summary/FileSummary';
import FileView from 'nodetypes/file/view/FileView';

// Gallery
import GalleryCard from 'nodetypes/gallery/card/GalleryCard';

// Base NODE components
import NodeCard from 'nodetypes/node/card/NodeCard';
import NodeSummary from 'nodetypes/node/summary/NodeSummary';
import NodeTile from 'nodetypes/node/tile/NodeTile';
import NodeView from 'nodetypes/node/view/NodeView';

// Photo
import PhotoCard from 'nodetypes/photo/card/PhotoCard';
import PhotoTile from 'nodetypes/photo/tile/PhotoTile';
import PhotoView from 'nodetypes/photo/view/PhotoView';

// Poll
import PollForm from 'nodetypes/poll/form/PollForm';
import PollSummary from 'nodetypes/poll/summary/PollSummary';
import PollTile from 'nodetypes/poll/tile/PollTile';
import PollView from 'nodetypes/poll/view/PollView';

// Profile
import ProfileSummary from 'nodetypes/profile/summary/ProfileSummary';

// Stream
import StreamForm from 'nodetypes/stream/form/StreamForm';
import StreamView from 'nodetypes/stream/view/StreamView';

// Video
import VideoCard from 'nodetypes/video/card/VideoCard';
import VideoSummary from 'nodetypes/video/summary/VideoSummary';
import VideoTile from 'nodetypes/video/tile/VideoTile';
import VideoView from 'nodetypes/video/view/VideoView';

// -----------------------------------------------------------------------------
// NodeTypeComponents (type -> components). Keep keys alphabetized.
// -----------------------------------------------------------------------------

/**
 * Central registry of per-type UI components.
 * Each entry can provide any of:
 * - cardComponent (for feeds)
 * - displayComponent
 * - formComponent (for editors)
 * - highlightComponent (for the node highlight tab)
 * - tileComponent (for grids)
 * - summaryComponent (for lists)
 * - viewComponent (for full node view)
 *
 * Resolution uses the concrete type key and then falls back to NODE.*.
 */
export const NodeTypeComponents = {

  ARTICLE: {
    highlightComponent: ArticleEditor,
  },

  DOCUMENT: {
    cardComponent: DocumentSummary,
    summaryComponent: DocumentSummary,
    viewComponent: DocumentView,
  },

  FILE: {
    cardComponent: FileSummary,
    summaryComponent: FileSummary,
    viewComponent: FileView,
  },

  GALLERY: {
    cardComponent: GalleryCard,
  },

  LIVE_ROOM: {
    tileComponent: NodeTile,
    viewComponent: NodeView,
  },

  LIVE_SESSION: {
    viewComponent: NodeView,
  },

  LIVE_STREAM: {
    formComponent: StreamForm,
    viewComponent: StreamView,
  },

  // Generic base NODE fallbacks.
  NODE: {
    cardComponent: NodeCard,
    summaryComponent: NodeSummary,
    tileComponent: NodeTile,
    viewComponent: NodeView,
  },

  PHOTO: {
    cardComponent: PhotoCard,
    tileComponent: PhotoTile,
    viewComponent: PhotoView,
  },

  POLL: {
    formComponent: PollForm,
    summaryComponent: PollSummary,
    tileComponent: PollTile,
    viewComponent: PollView,
  },

  USER: {
    summaryComponent: ProfileSummary,
  },

  VIDEO: {
    cardComponent: VideoCard,
    summaryComponent: VideoSummary,
    tileComponent: VideoTile,
    viewComponent: VideoView,
  },

};

// -----------------------------------------------------------------------------
// Enum + resolver
// -----------------------------------------------------------------------------

export const ComponentType = Object.freeze({
  CARD: 'CARD',
  DISPLAY: 'DISPLAY',
  FEED: 'FEED',
  FORM: 'FORM',
  GRID: 'GRID',
  HIGHLIGHT: 'HIGHLIGHT',
  LIST: 'LIST',
  ROW: 'ROW',
});

const COMPONENT_PROP = {
  [ComponentType.CARD]: 'cardComponent',
  [ComponentType.DISPLAY]: 'displayComponent',
  [ComponentType.FEED]: 'viewComponent',
  [ComponentType.FORM]: 'formComponent',
  [ComponentType.GRID]: 'tileComponent',
  [ComponentType.HIGHLIGHT]: 'highlightComponent',
  [ComponentType.LIST]: 'summaryComponent',
  [ComponentType.ROW]: 'rowComponent',
};

// Simple memo: (type, componentType) -> component | null
const _resolveCache = new Map();
const _cacheKey = (scopeKey, rev, type, componentType) => `${scopeKey}::${rev}::${componentType}::${type || ''}`;

/**
 * resolveComponent
 * 
 * Resolves a component for a given node type and component type.
 *
 * @param {string} type - The node type to resolve the component for.
 * @param {ComponentType} componentType - The type of component to resolve.
 * @param {Function} getType - Function to get node type definitions by type key.
 * @param {object} [options] - Additional options.
 * @param {boolean} [options.quiet=false] - Suppress warnings.
 * @param {boolean} [options.allowNullIfMissingBase=false]
 *        If true, and NODE has no fallback for this component type, just return null silently.
 */
export function resolveComponent(type, componentType, getType, options = {}) {
  const {
    quiet = false,
    allowNullIfMissingBase = false,
  } = options;

  const prop = COMPONENT_PROP[componentType];
  if (!type || !prop || typeof getType !== 'function') return null;

  if (typeof type !== 'string') return null;

  const typeKey = type;
  if (!typeKey) return null;

  const cacheMeta = getNodeTypesCache();
  const scopeKey = cacheMeta?.scopeKey || 'ROOT';
  const rev = cacheMeta?.rev || '';

  // Cache hit?
  const ck = _cacheKey(scopeKey, rev, typeKey, componentType);
  if (_resolveCache.has(ck)) return _resolveCache.get(ck);

  const reg = NodeTypeComponents[typeKey];
  if (reg && reg[prop]) {
    _resolveCache.set(ck, reg[prop]);
    return reg[prop];
  }

  // Try NODE fallback for this componentType
  const nodeReg = NodeTypeComponents.NODE;
  if (nodeReg && nodeReg[prop]) {
    _resolveCache.set(ck, nodeReg[prop]);
    return nodeReg[prop];
  }

  // Nothing found: either be quiet (return null) or warn once.
  if (!quiet && !allowNullIfMissingBase && process.env.NODE_ENV === 'development') {
    console.warn(
      `[resolveComponent] No component for ${componentType} (type="${type}"), and NODE has no ${prop}.`
    );
  }

  _resolveCache.set(ck, null);
  return null;
}

/**
 * Convenience: resolve a FORM component but quietly return null if none exists
 * (including when NODE has no formComponent).
 */
export function resolveFormComponent(type, getType, options = {}) {
  return resolveComponent(type, ComponentType.FORM, getType, {
    quiet: true,
    allowNullIfMissingBase: true,
    ...options,
  });
}


